import type { Swiper } from 'swiper';
import { ProductFilter, ProductSort } from 'types/schema';

export const MAX_PRODUCTS = 50;
export const INITIAL_PAGE_SIZE = 10;
export const MAX_FARM_TO_BIZ = 24;

const TOTAL_RESULTS_KEY_PREFIX = 'homeCarousel:totalResults';

// `querySignature` là chữ ký của câu query (xem queryKey trong HomePage/saga), KHÔNG
// phải section.key: prefetch chạy trước khi widget config về nên chưa biết key.
export const getTotalResultsStorageKey = (region: string, querySignature: string) =>
  `${TOTAL_RESULTS_KEY_PREFIX}:${region}:${querySignature}`;

export const getCachedTotalResults = (storageKey: string): number => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return 0;

    const parsed = Number(raw);

    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  } catch {
    return 0;
  }
};

export const setCachedTotalResults = (storageKey: string, totalResults: number) => {
  try {
    if (totalResults > 0) {
      window.localStorage.setItem(storageKey, String(totalResults));
    }
  } catch {
    // ignore quota / private mode
  }
};

const DAY_PLAN_KEY_PREFIX = 'homeCarousel:dayPlan';

export interface IDayPlan {
  seed: number;
  basePage: number;
  totalPages: number;
  /** Số trang ĐẦY — miền xoay của basePage. Xem calcFullPages. */
  fullPages: number;
}

export const getDayPlanKey = (region: string, lang: string, sectionKey: string) =>
  `${DAY_PLAN_KEY_PREFIX}:${region}:${lang}:${sectionKey}`;

export const getDayPlan = (storageKey: string, seed: number): IDayPlan | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || parsed.seed !== seed) return null;

    if (!Number.isInteger(parsed.basePage) || parsed.basePage < 0) return null;

    if (!Number.isInteger(parsed.totalPages) || parsed.totalPages < 1) return null;

    return parsed as IDayPlan;
  } catch {
    return null;
  }
};

export const setDayPlan = (storageKey: string, plan: IDayPlan) => {
  try {
    if (plan.totalPages < 1 || plan.basePage < 0) return;

    window.localStorage.setItem(storageKey, JSON.stringify(plan));
  } catch {
    // ignore quota / private mode
  }
};

export const getSeededBasePage = (seed: number, totalPages: number) => {
  const pages = Math.max(1, totalPages);

  return seed % pages;
};

export const resolveBasePage = (dayPlan: IDayPlan | null, seed: number, cachedTotalPages: number) => {
  if (dayPlan) return dayPlan.basePage;

  return cachedTotalPages > 0 ? getSeededBasePage(seed, cachedTotalPages) : 0;
};

export const getNextPage = (currentPage: number, totalPages: number) => {
  const pages = Math.max(1, totalPages);

  return (Math.max(0, currentPage) + 1) % pages;
};

export const calcTotalPages = (totalResults: number, pageSize: number = INITIAL_PAGE_SIZE) =>
  Math.max(1, Math.ceil(Math.max(0, totalResults) / pageSize));

/** Số trang đầy — miền xoay của basePage. Phải khớp server/utils/homeCarouselSeed.js. */
export const calcFullPages = (totalResults: number, pageSize: number = INITIAL_PAGE_SIZE) =>
  Math.max(1, Math.floor(Math.max(0, totalResults) / pageSize));

export const trimToFullPages = <T>(items: T[], pageSize: number): T[] => {
  if (!items?.length || pageSize <= 0) return items || [];

  if (items.length <= pageSize) return items;

  const fullCount = Math.floor(items.length / pageSize) * pageSize;

  return fullCount > 0 ? items.slice(0, fullCount) : items;
};

const FIRST_SHOWCASE_HINT_KEY_PREFIX = 'homeCarousel:firstShowcase';

export interface IFirstShowcaseHint {
  key: string;
  sort: ProductSort[];
  filter: ProductFilter;
  id?: string | null;
}

// `prefetchFirstShowcase` (HomePage/saga.ts) bắn TRƯỚC khi widgets về nên không thể
// biết widget đầu tiên là widget nào — nó đoán bằng cấu hình đã ghi lại ở LẦN TẢI
// TRANG GẦN NHẤT. Đoán sai (BE đổi thứ tự widget) thì `sameQuery` tự phát hiện lệch,
// rơi về fetch thật, và `loadShowcaseItems` sẽ ghi đè hint đúng cho lần sau — tự lành,
// cùng triết lý với cache `totalPages` ở trên. Khoá theo region, KHÔNG theo lang: widget
// nào hiển thị là quyết định của BE theo region (`widgetsRedesign(regions: [String])`
// không nhận lang), chỉ text hiển thị mới đổi theo ngôn ngữ.
export const getFirstShowcaseHintKey = (region: string) => `${FIRST_SHOWCASE_HINT_KEY_PREFIX}:${region}`;

export const getCachedFirstShowcaseHint = (storageKey: string): IFirstShowcaseHint | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed.key !== 'string' || !Array.isArray(parsed.sort)) return null;

    return parsed as IFirstShowcaseHint;
  } catch {
    return null;
  }
};

export const setCachedFirstShowcaseHint = (storageKey: string, hint: IFirstShowcaseHint) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(hint));
  } catch {
    // ignore quota / private mode
  }
};

export const LOAD_MORE_PREFETCH_GROUPS = 2;

export const MAX_LOAD_MORE_PROBES = 3;

const END_SNAP_EPSILON_PX = 16;

export const dropDegenerateEndSnap = (swiper: Swiper) => {
  if (swiper.destroyed) return;

  const grid = swiper.snapGrid;
  const last = grid.length - 1;

  if (last <= 1 || grid[last] - grid[last - 1] > END_SNAP_EPSILON_PX) return;

  grid.pop();

  const endSnap = grid[grid.length - 1];

  const overshoot = -swiper.translate - endSnap;

  if (!swiper.animating && overshoot > 0 && overshoot <= END_SNAP_EPSILON_PX) {
    swiper.setTranslate(-endSnap);
  }

  swiper.updateProgress();
  swiper.navigation?.update();
};

export const getProductSlidesPerGroup = (showcaseId: string, width: number) => {
  if (width < 768) {
    return 2;
  }

  if (showcaseId === 'BEST_PRICES') {
    return 4;
  }

  return 5;
};
